import React, { useEffect, useReducer, useRef } from "react";

import classnames from "classnames";
import ReactDOMServer from "react-dom/server";
import { useTranslation } from "react-i18next";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { MetaTags } from "react-meta-tags";
import { Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useSelector, useDispatch } from "react-redux";
import { useToasts } from "react-toast-notifications";
import { encode } from "js-base64";
import html2canvas from "html2canvas";

import useOutsideClicks from "helpers/useOutsideClicks";
import ValuePlaceholder from "./InvoiceTemplateViewer/ValuePlaceholder";
import { config as rooTemplateConfig } from "./rootInvoiceTemplateConfig";
import { tabsName, RootInvoiceTemplate, NormalInvoiceTemplate } from "./const";
import { reducer, initialState } from "./InvoiceTemplateEditorReducer";
import { InvoiceTemplateProvider } from "./InvoiceTemplateContext";
import InvoiceTemplateViewer from "./InvoiceTemplateViewer";
import InvoiceTemplateGeneralEditor from "./InvoiceTemplateEditor/InvoiceTemplateGeneralEditor";
import InvoiceTemplateImageEditor from "./InvoiceTemplateEditor/InvoiceTemplateImageEditor";
import InvoiceTemplateDetailEditor from "./InvoiceTemplateEditor/InvoiceTemplateDetailEditor";

import {
  createInvoiceTemplate,
  updateInvoiceTemplate,
  getDetailInvoiceTemplate,
  resetCreateInvoiceTemplateState,
} from "store/actions";

function useQuery() {
  const { search } = useLocation();

  return React.useMemo(() => new URLSearchParams(search), [search]);
}

const useInvoiceTemplateDetail = ({ id }) => {
  const dispatch = useDispatch();
  const { detail, loadingDetail } = useSelector(state => state.invoiceTemplate);

  useEffect(() => {
    if (id) {
      dispatch(getDetailInvoiceTemplate(id));
    }
  }, [dispatch, id]);

  return { templateDetail: detail, loadingDetail };
};

const useUpdateInvoiceTemplateConfig = ({
  templateDetail,
  isCreateRootTemplate,
  dispatch,
  rootTemplateId,
}) => {
  useEffect(() => {
    const templateConfig = isCreateRootTemplate
      ? rooTemplateConfig
      : templateDetail
      ? rootTemplateId
        ? { ...templateDetail.config, isRootTemplate: false }
        : templateDetail.config
      : null;

    dispatch({
      type: "loadInvoiceTemplateConfig",
      payload: { templateConfig },
    });
  }, [templateDetail, dispatch, rootTemplateId, isCreateRootTemplate]);
};

const InvoiceTemplateEditor = props => {
  const query = useQuery();
  const history = useHistory();
  const appDispatch = useDispatch();
  const { addToast } = useToasts();
  const { t } = useTranslation(["invoiceTemplate"]);

  const { templateId } = useParams();
  const rootTemplateId = query.get("root-template-id");
  const isCreateRootTemplate = Boolean(query.get("create-root-template"));

  const [state, dispatch] = useReducer(reducer, initialState);

  const invoiceTemplate = useSelector(state => state.invoiceTemplate);
  const { detail, create, loadingCreate, loadingUpdate, update } =
    invoiceTemplate;

  const id = templateId || rootTemplateId;
  const { templateDetail } = useInvoiceTemplateDetail({ id });

  useUpdateInvoiceTemplateConfig({
    templateDetail,
    isCreateRootTemplate,
    dispatch,
    rootTemplateId,
  });

  useEffect(() => {
    if (update?.success) {
      addToast("Cập nhật mẫu hóa đơn thành công", {
        appearance: "success",
        autoDismiss: true,
      });
    }
  }, [update, addToast]);

  useEffect(() => {
    if (create?.success) {
      addToast("Tạo mới mẫu hóa đơn thành công", {
        appearance: "success",
        autoDismiss: true,
      });

      appDispatch(resetCreateInvoiceTemplateState());

      setTimeout(() => {
        history.push(`/invoice-templates`);
      }, 1000);
    }
  }, [create, addToast]);

  const dashboard = useSelector(state => state.Dashboard);
  const { finalBuInfo } = dashboard;

  const updateInvoiceData = (key, value) => {
    dispatch({
      type: "updateInvoiceData",
      payload: { path: key, value },
    });
  };

  useEffect(() => {
    if (
      finalBuInfo &&
      state?.invoiceData &&
      (isCreateRootTemplate || rootTemplateId)
    ) {
      const {
        ten,
        dchi,
        mst,
        website,
        fax,
        sdthoai,
        tnhang,
        stknhang,
        dctdtu,
      } = finalBuInfo;

      const updateFields = {
        seller_name: ten,
        seller_address: dchi,
        seller_tax_code: mst,
        seller_tel: sdthoai,
        seller_email: dctdtu,
        seller_fax: fax,
        seller_website: website,
        seller_account_number: `${stknhang} ${tnhang}`,
      };

      Object.keys(updateFields).forEach(key => {
        const index = state.invoiceData.sellerSection.fields.findIndex(
          item => item.name === key
        );

        updateInvoiceData(
          `sellerSection.fields[${index}].value`,
          updateFields[key]
        );
      });
    }
  }, [finalBuInfo, isCreateRootTemplate, rootTemplateId]);

  const viewerRef = useRef(null);
  const viewerContainerRef = useRef(null);
  useOutsideClicks(
    viewerRef,
    () => {
      dispatch({ type: "deselectElements" });
    },
    viewerContainerRef
  );

  const switchTab = tab => {
    dispatch({ type: "switchTab", payload: { tab } });
  };

  const generateInvoiceTemplateHTML = ({ withTableSummary, invoiceForm }) => {
    return ReactDOMServer.renderToStaticMarkup(
      <html lang="en">
        <head></head>
        <body>
          <InvoiceTemplateProvider
            value={{
              state: {
                ...state,
                invoiceForm,
              },
              dispatch,
              showValuePlaceHolder: true,
              withTableSummary,
            }}
          >
            <InvoiceTemplateViewer data={state.invoiceData} />
          </InvoiceTemplateProvider>
        </body>
      </html>
    );
  };

  const download = () => {
    const htmlCode = generateInvoiceTemplateHTML({
      withTableSummary: true,
      invoiceForm: "Electronic",
    });

    var element = document.createElement("a");
    element.setAttribute(
      "href",
      "data:text/plain;charset=utf-8," + encodeURIComponent(htmlCode)
    );
    const time = Date.now();
    element.setAttribute("download", `invoice-template-${time}.html`);
    element.style.display = "none";
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const goBack = () => {
    if (templateId) {
      history.push(`/invoice-templates`);
    } else {
      history.push(`/invoice-templates/create/select-template`);
    }
  };

  const reset = () => {
    dispatch({ type: "resetInvoiceTemplateConfig" });
  };

  const getTableContent = () => {
    const { border, fields, cellStyle } = state.invoiceData.invoiceTableSection;

    const borderRowStyle = border.enabled ? border.style : {};

    return ReactDOMServer.renderToStaticMarkup(
      <>
        <InvoiceTemplateProvider
          value={{ state, dispatch, showValuePlaceHolder: true }}
        >
          <tr style={borderRowStyle}>
            {fields.map((field, index) => (
              <td key={index} style={cellStyle}>
                <ValuePlaceholder
                  placeholderKey={field.name}
                ></ValuePlaceholder>
              </td>
            ))}
          </tr>
        </InvoiceTemplateProvider>
      </>
    );
  };

  const prepareDataForSave = () => {
    const htmlContent = encode(
      generateInvoiceTemplateHTML({
        withTableSummary: true,
        invoiceForm: "Electronic",
      })
    );
    const htmlContentWithoutTotal = encode(
      generateInvoiceTemplateHTML({
        withTableSummary: false,
        invoiceForm: "Electronic",
      })
    );
    const htmlContentConvert = encode(
      generateInvoiceTemplateHTML({
        withTableSummary: true,
        invoiceForm: "Conversion",
      })
    );
    const htmlContentConvertWithoutTotal = encode(
      generateInvoiceTemplateHTML({
        withTableSummary: false,
        invoiceForm: "Conversion",
      })
    );

    const isUpdate = Boolean(templateId && templateDetail && templateDetail.id);
    const khhdon = `${state.invoiceData.invoiceSection.serialNo.prefix}${state.invoiceData.invoiceSection.serialNo.value}`;

    const sections = ["customerSection", "sellerSection", "signatureSection"];
    let contentParams = Object.keys(state.invoiceData)
      .filter(key => sections.includes(key))
      .map(key => {
        return state.invoiceData[key].fields.map(field => ({
          name: field.name,
          defaultValue: field.value,
        }));
      });

    state.invoiceData.invoiceTableSection.summaryFields.map(field => {
      contentParams.push({
        name: field.name,
        defaultValue: field.value,
      });
    });

    contentParams.push({
      name: state.invoiceData.invoiceSection.serialNo.name,
      defaultValue: state.invoiceData.invoiceSection.serialNo.value,
    });

    contentParams.push({
      name: state.invoiceData.invoiceSection.no.name,
      defaultValue: state.invoiceData.invoiceSection.no.value,
    });

    contentParams.push(
      {
        name: "day",
        defaultValue: null,
      },
      {
        name: "month",
        defaultValue: null,
      },
      {
        name: "year",
        defaultValue: null,
      },
      {
        name: "search_code",
      },
      {
        name: "qr_code",
      },
      {
        name: "converter_date",
      }
    );

    contentParams = contentParams.flat();
    const contentTable = encode(getTableContent());

    if (isUpdate) {
      const isRootTemplate = detail.type === "root";
      return {
        type: detail.type,
        khmhdon: detail.khmhdon,
        khhdon: khhdon,
        code: detail.code,
        config: { ...state.invoiceData, isRootTemplate },
        content: htmlContent,
        content_without_total: htmlContentWithoutTotal,
        content_convert: htmlContentConvert,
        content_convert_without_total: htmlContentConvertWithoutTotal,
        params: contentParams,
        content_table: contentTable,
        bu_code: detail.bu_code,
      };
    }

    if (isCreateRootTemplate) {
      const khmhdon = state.invoiceData.invoiceSection.serialNo.type;

      return {
        type: RootInvoiceTemplate,
        khmhdon,
        khhdon,
        config: { ...state.invoiceData, isRootTemplate: true },
        content: htmlContent,
        content_without_total: htmlContentWithoutTotal,
        content_convert: htmlContentConvert,
        content_convert_without_total: htmlContentConvertWithoutTotal,
        content_table: contentTable,
        params: contentParams,
      };
    }

    // Create new template from root template
    return {
      type: NormalInvoiceTemplate,
      khhdon,
      khmhdon: templateDetail.khmhdon,
      config: {
        ...state.invoiceData,
        isRootTemplate: false,
        invoiceTemplateRootCode: templateDetail.code,
        invoiceTemplateRootId: templateDetail.id,
      },
      content: htmlContent,
      content_without_total: htmlContentWithoutTotal,
      content_convert: htmlContentConvert,
      content_convert_without_total: htmlContentConvertWithoutTotal,
      content_table: contentTable,
      params: contentParams,
    };
  };

  const save = () => {
    const data = prepareDataForSave();

    if (templateId && templateDetail) {
      appDispatch(updateInvoiceTemplate(templateId, data));
    } else {
      appDispatch(createInvoiceTemplate(data));
    }
  };

  const captureInvoiceTemplate = () => {
    var element = document.querySelector(".invoice-template-wrapper");
    html2canvas(element).then(function (canvas) {
      var imageData = canvas.toDataURL("image/png");
      var newData = imageData.replace(
        /^data:image\/png/,
        "data:application/octet-stream"
      );

      const time = Date.now();
      var element = document.createElement("a");
      element.setAttribute("href", newData);
      element.setAttribute("download", `invoice-template-thumb${time}.png`);
      element.style.display = "none";
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
    });
  };

  useEffect(() => {
    document.querySelector(".main-content").classList.add("vh-100");
    return () => {
      document.querySelector(".main-content").classList.remove("vh-100");
    };
  }, []);

  return (
    <>
      <MetaTags>
        <title>
          {t("templateEditor.title.pageTitle", {
            name: "VIN-GTGT-02A4-TT92-01GTKT",
          })}
        </title>
      </MetaTags>

      <InvoiceTemplateProvider
        value={{ state, dispatch, withTableSummary: true }}
      >
        <DndProvider backend={HTML5Backend}>
          <div className="page-content invoice-template-editor d-flex flex-row">
            {!state.invoiceData ? (
              <div className="d-flex flex-column w-100 align-items-center mt-5">
                <div
                  className="spinner-border text-primary"
                  style={{ width: "3rem", height: "3rem" }}
                  role="status"
                >
                  <span className="visually-hidden">Loading...</span>
                </div>
                <div className="mt-3">Đang tải</div>
              </div>
            ) : (
              <>
                {/* Start left column */}
                <div className="left-column flex-shrink-0 d-flex flex-column h-100">
                  <div
                    className="flex-shrink-0 d-flex align-items-center ps-3"
                    style={{ height: "42px" }}
                  >
                    <h4 className="mb-0 font-size-18">
                      {templateId && templateDetail ? (
                        <span>Hóa đơn mẫu: {templateDetail.code}</span>
                      ) : (
                        <span>Tạo mới hóa đơn mẫu</span>
                      )}
                    </h4>
                  </div>

                  <div className="d-flex flex-column flex-grow-1 overflow-hidden">
                    <Nav className="flex-shrink-0" tabs>
                      <NavItem>
                        <NavLink
                          style={{ cursor: "pointer" }}
                          className={classnames({
                            active: state.activeTab === tabsName.general,
                          })}
                          onClick={() => {
                            switchTab(tabsName.general);
                          }}
                        >
                          {t("templateEditor.title.generalInfo")}
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          style={{ cursor: "pointer" }}
                          className={classnames({
                            active: state.activeTab === tabsName.image,
                          })}
                          onClick={() => {
                            switchTab(tabsName.image);
                          }}
                        >
                          {t("templateEditor.title.logoAndBackground")}
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          style={{ cursor: "pointer" }}
                          className={classnames({
                            active: state.activeTab === tabsName.detail,
                          })}
                          onClick={() => {
                            switchTab(tabsName.detail);
                          }}
                        >
                          {t("templateEditor.title.detailOptions")}
                        </NavLink>
                      </NavItem>
                    </Nav>
                    <TabContent
                      activeTab={state.activeTab}
                      className="py-3 text-muted flex-grow-1 overflow-auto"
                    >
                      <TabPane tabId={tabsName.general}>
                        <InvoiceTemplateGeneralEditor
                          isCreateRootTemplate={isCreateRootTemplate}
                        />
                      </TabPane>
                      <TabPane tabId={tabsName.image}>
                        <InvoiceTemplateImageEditor />
                      </TabPane>
                      <TabPane tabId={tabsName.detail}>
                        <InvoiceTemplateDetailEditor />
                      </TabPane>
                    </TabContent>
                  </div>

                  <div
                    className="flex-shrink-0 d-flex justify-content-center align-items-center left-column-footer"
                    style={{ height: "50px" }}
                  >
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={goBack}
                    >
                      <span>Quay lại</span>
                    </button>

                    <button
                      type="button"
                      className="btn btn-info ms-3"
                      onClick={() => reset()}
                    >
                      <i className="bx bx-rotate-left me-2 font-size-16 align-middle" />
                      <span>Khôi phục ban đầu</span>
                    </button>

                    <button
                      type="button"
                      className="btn btn-primary ms-3"
                      onClick={() => save()}
                    >
                      {loadingUpdate || loadingCreate ? (
                        <>
                          <span
                            className="spinner-grow spinner-grow-sm"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          Đang lưu...
                        </>
                      ) : (
                        <>
                          <i className="bx bx-download me-2 font-size-16 align-middle" />
                          <span>Lưu thông tin</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
                {/* End left column */}

                {/* Start right column */}
                <div className="flex-grow-1 d-flex flex-column">
                  {/* Start invoice viewer toolbar */}
                  <div
                    className="flex-shrink-0 d-flex justify-content-center align-items-center"
                    style={{ height: "42px" }}
                  >
                    <div className="form-check me-4">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="mau-hoa-don"
                        id="mau-hoa-don-dien-tu"
                        checked={state.invoiceForm === "Electronic"}
                        onChange={() => {
                          dispatch({
                            type: "switchInvoiceForm",
                            payload: "Electronic",
                          });
                        }}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="mau-hoa-don-dien-tu"
                      >
                        Mẫu hóa đơn điện tử
                      </label>
                    </div>

                    <div className="form-check me-4">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="mau-hoa-don"
                        id="mau-hoa-don-chuyen-doi"
                        value="hoa-don-chuyen-doi"
                        checked={state.invoiceForm === "Conversion"}
                        onChange={() => {
                          dispatch({
                            type: "switchInvoiceForm",
                            payload: "Conversion",
                          });
                        }}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="mau-hoa-don-chuyen-doi"
                      >
                        Mẫu hóa đơn chuyển đổi
                      </label>
                    </div>

                    <button
                      type="button"
                      className="btn btn-info btn-sm"
                      onClick={() => download()}
                    >
                      <i className="bx bx-download me-2 font-size-16 align-middle" />
                      <span>Tải HTML</span>
                    </button>

                    {(isCreateRootTemplate ||
                      state.invoiceData.isRootTemplate) && (
                      <button
                        type="button"
                        className="btn btn-info btn-sm ms-4"
                        onClick={() => captureInvoiceTemplate()}
                      >
                        <i className="bx bx-download me-2 font-size-16 align-middle" />
                        <span>Chụp hình</span>
                      </button>
                    )}
                  </div>
                  {/* End invoice viewer toolbar */}

                  {/* Start invoice viewer */}
                  <div className="flex-grow-1 overflow-auto bg-white">
                    <div className="viewer-container" ref={viewerContainerRef}>
                      <div className="viewer-wrapper" ref={viewerRef}>
                        <InvoiceTemplateViewer data={state.invoiceData} />
                      </div>
                    </div>
                  </div>
                  {/* End invoice viewer */}
                </div>
                {/* End right column */}
              </>
            )}
          </div>
        </DndProvider>
      </InvoiceTemplateProvider>
    </>
  );
};

export default InvoiceTemplateEditor;
